import { useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronDown, IoSearch } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';

import { PHONE_COUNTRIES, normalizePhoneSearch } from '../../utils/phoneCountries';
import { getLocalizedRegionName } from '../../i18n';
import styles from './countryphoneselect.module.css';

function CountryPhoneSelect({
  value,
  onChange,
  countries = PHONE_COUNTRIES,
  label,
  disabled = false,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selectedCountry = value || countries[0];

  const filteredCountries = useMemo(() => {
    const normalizedQuery = normalizePhoneSearch(query);
    if (!normalizedQuery) return countries;
    const queryDigits = normalizedQuery.replace(/\D/g, '');

    return countries.filter((country) => {
      const localizedName = getLocalizedRegionName(country.iso, i18n.resolvedLanguage);
      const searchText = normalizePhoneSearch(
        `${country.iso} ${country.name} ${localizedName} ${country.ddi} ${country.searchText || ''}`
      );
      const matchesNameOrIso = searchText.includes(normalizedQuery);
      const matchesDdi = queryDigits ? country.ddi.includes(queryDigits) : false;

      return matchesNameOrIso || matchesDdi;
    });
  }, [countries, i18n.resolvedLanguage, query]);

  useEffect(() => {
    if (!open) return undefined;

    searchRef.current?.focus();

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = (country) => {
    onChange?.(country);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={styles.wrapper} ref={rootRef}>
      <label className={styles.label} htmlFor="country-phone-search">
        {label || t('auth.country')}
      </label>

      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={styles.flag} aria-hidden="true">{selectedCountry.flag}</span>
        <span className={styles.ddi}>+{selectedCountry.ddi}</span>
        <IoChevronDown className={styles.chevron} size={16} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchBox}>
            <IoSearch size={16} aria-hidden="true" />
            <input
              id="country-phone-search"
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('phoneCountry.search')}
              className={styles.searchInput}
              autoComplete="off"
            />
          </div>

          <div className={styles.options} role="listbox">
            {filteredCountries.map((country) => (
              <button
                key={country.iso}
                type="button"
                className={`${styles.option} ${country.iso === selectedCountry.iso ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(country)}
                role="option"
                aria-selected={country.iso === selectedCountry.iso}
              >
                <span className={styles.optionFlag} aria-hidden="true">{country.flag}</span>
                <span className={styles.optionName}>
                  {getLocalizedRegionName(country.iso, i18n.resolvedLanguage) || country.name}
                </span>
                <span className={styles.optionDdi}>+{country.ddi}</span>
              </button>
            ))}

            {filteredCountries.length === 0 && (
              <div className={styles.empty}>{t('phoneCountry.empty')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CountryPhoneSelect;
